import React, { memo, useState, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';

import { useClasses } from '../../../utils/overrides';
import { createUseStyles } from '../../../utils/styles';
import Modal from '../Modal';
import ImageCrop from './ImageCrop';
import styles from './styles';

const useStyles = createUseStyles(styles, 'ModalCrop');

const ModalCrop = memo(
    ({
        aspect,
        cancelText,
        classes: classesProp,
        confirmText,
        file,
        isOpen,
        onAccept,
        onCancel,
        overrides,
        title,
        ...props
    }) => {
        const classes = useClasses(useStyles, classesProp);
        const [crop, setCrop] = useState({});
        const imgRef = useRef(null);

        const src = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);

        const handleConfirm = useCallback(() => {
            const img = imgRef.current;
            if (!img || !crop.width || !crop.height) return;

            const scaleX = img.naturalWidth / img.width;
            const scaleY = img.naturalHeight / img.height;

            const canvas = document.createElement('canvas');
            canvas.width = crop.width;
            canvas.height = crop.height;

            canvas
                .getContext('2d')
                .drawImage(
                    img,
                    crop.x * scaleX,
                    crop.y * scaleY,
                    crop.width * scaleX,
                    crop.height * scaleY,
                    0,
                    0,
                    crop.width,
                    crop.height,
                );

            const dotIdx = file.name.lastIndexOf('.');
            const hasExt = dotIdx > 0;
            const name = hasExt ? file.name.slice(0, dotIdx) : file.name;
            const ext = hasExt ? file.name.slice(dotIdx + 1) : '';
            const base = name.includes('_crop') ? name : `${name}_crop`;

            canvas.toBlob(
                (blob) => {
                    blob.name = hasExt ? `${base}.${ext}` : base;
                    onAccept(blob);
                },
                file.type,
                1,
            );
        }, [crop, file, onAccept]);

        const handleImageLoad = useCallback(
            (e) => {
                const img = e.target;
                imgRef.current = img;

                const size = Math.min(img.width, img.height) * 0.6;
                let width = size;
                let height = size;

                if (aspect) {
                    if (aspect > 1) {
                        width = Math.min(height * aspect, img.width * 0.9);
                        height = width / aspect;
                    } else {
                        height = Math.min(width / aspect, img.height * 0.9);
                        width = height * aspect;
                    }
                }

                setCrop({
                    x: (img.width - width) / 2,
                    y: (img.height - height) / 2,
                    width,
                    height,
                    unit: 'px',
                });
            },
            [aspect],
        );

        return (
            <Modal
                cancelText={cancelText}
                className={classes.cropModal}
                confirmText={confirmText}
                isOpen={isOpen}
                onCancel={onCancel}
                onConfirm={handleConfirm}
                overrides={overrides}
                title={title}
                useCornerClose={false}
                {...props}
            >
                <div className={classes.cropModalContent}>
                    {src && (
                        <ImageCrop
                            aspect={aspect}
                            className={classes.cropCanvas}
                            crop={crop}
                            onChange={setCrop}
                            onLoad={handleImageLoad}
                            src={src}
                        />
                    )}
                </div>
            </Modal>
        );
    },
);

ModalCrop.propTypes = {
    aspect: PropTypes.number,
    cancelText: PropTypes.string,
    classes: PropTypes.object,
    confirmText: PropTypes.string,
    file: PropTypes.object,
    isOpen: PropTypes.bool,
    onAccept: PropTypes.func,
    onCancel: PropTypes.func,
    overrides: PropTypes.object,
    title: PropTypes.string,
};

export default ModalCrop;
